import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getApiBase } from './config/apiBase';
import { authService } from './services/authService';
import RichTextEditor from './components/RichTextEditor';
import LyricAIPopup from './LyricAIPopup';
import JiraTicketLink from './components/JiraTicketLink';

const apiUrl = getApiBase();

const authenticatedFetch = async (url, options = {}) => {
  const token = await authService.getAccessToken();
  return fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
};

// Reduce HTML to plain text for use as model context.
const htmlToText = (html) => String(html || '')
  .replace(/<\s*br\s*\/?>/gi, '\n')
  .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
  .replace(/<[^>]+>/g, '')
  .replace(/&nbsp;/g, ' ')
  .replace(/&amp;/g, '&')
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/&#39;/g, "'")
  .replace(/&quot;/g, '"')
  .replace(/\n{3,}/g, '\n\n')
  .trim();

/**
 * Story sub-flow for Step 3 of the SDLC flow. The user picks a parent Epic
 * (from the Epics linked in Step 2, or any Epic in the project), then works the
 * exact same way the Jira Issue Manager does: suggest stories from the Epic with
 * AI, edit each story's title / description (same rich text editor) / acceptance
 * criteria, generate or delete individual stories, then create them in Jira
 * under the Epic. Created stories are linked back into the flow's Story bucket.
 */
const CreateStoriesPanel = ({ flow, selectedProject, selectedModel, onStoriesCreated, onSaveDrafts }) => {
  const linkedEpics = flow?.linkedTickets?.epics || [];

  // Parent Epic picker (searchable, project-scoped, seeded with linked Epics).
  const [epicOptions, setEpicOptions] = useState([]);
  const [epicSearch, setEpicSearch] = useState('');
  const [epicsLoading, setEpicsLoading] = useState(false);
  const [selectedEpicKey, setSelectedEpicKey] = useState(linkedEpics[0] || '');

  const [epic, setEpic] = useState(null);
  const [epicLoading, setEpicLoading] = useState(false);
  const [epicError, setEpicError] = useState(null);

  const [suggestedStories, setSuggestedStories] = useState([]);
  const [expandedStories, setExpandedStories] = useState({});
  const storyRefs = useRef({});
  // Monotonic id generator so each story has a stable key independent of its
  // array position — required so React keys, expand/collapse state and editor
  // refs don't shift to the wrong story when stories are deleted or reordered.
  const uidRef = useRef(0);
  const nextUid = () => `st_${uidRef.current++}`;

  const [suggesting, setSuggesting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Optional rule file applied to per-story "Generate description" (✨) calls:
  // a built-in template (by name) or a user-uploaded JSON — mirrors the same
  // feature on the Create-Epic panel (CreateEpicPanel.jsx), which this was
  // missing even though the Design Output tool (IssueForm.jsx) has always had it.
  const [ruleTemplates, setRuleTemplates] = useState([]);
  const [ruleTemplate, setRuleTemplate] = useState('');
  const [customRules, setCustomRules] = useState(null);
  const [customRulesName, setCustomRulesName] = useState('');
  const rulesInputRef = useRef(null);

  // Persisted draft Stories (per parent Epic) come from the flow. Held in a ref
  // so the draft-load effect can read the latest without re-running every time
  // the flow is saved elsewhere.
  const storyDraftsRef = useRef(flow?.storyDrafts || {});
  useEffect(() => { storyDraftsRef.current = flow?.storyDrafts || {}; }, [flow?.storyDrafts]);
  // JSON signature of the drafts last persisted for the selected Epic, so the
  // autosave effect can skip redundant writes (notably right after a load).
  const lastSavedSigRef = useRef(null);
  // Strip transient, non-persisted fields before saving/comparing a draft.
  const stripDraft = ({ _uid, isGenerating, ...rest }) => rest;

  // Load the project's Epics for the picker, and make sure any already-linked
  // Epics are always selectable even if they're not on the first page.
  useEffect(() => {
    if (!selectedProject) { setEpicOptions([]); return undefined; }
    let cancelled = false;
    (async () => {
      setEpicsLoading(true);
      try {
        // Use the dedicated /api/epics endpoint (server-side JQL filtered to
        // issuetype = Epic) instead of fetching every issue and filtering on the
        // client, which is slow for large projects.
        const res = await authenticatedFetch(`${apiUrl}/api/epics?projectKey=${encodeURIComponent(selectedProject)}`);
        const data = await res.json();
        if (!cancelled && res.ok && data.success) {
          // Normalise to { key, fields: { summary } } so the picker render and
          // merge logic below stay unchanged.
          const epics = (data.epics || []).map((e) => ({ key: e.key, fields: { summary: e.summary || '' } }));
          setEpicOptions(epics);
        }
      } catch {
        /* non-fatal */
      } finally {
        if (!cancelled) setEpicsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedProject]);

  // Keep the selected Epic valid as the linked set changes.
  useEffect(() => {
    if (linkedEpics.length && !selectedEpicKey) setSelectedEpicKey(linkedEpics[0]);
  }, [linkedEpics, selectedEpicKey]);

  // Load the selected Epic's summary + description (used as context for
  // suggestions and per-story generation).
  const loadEpic = useCallback(async (key) => {
    if (!key) { setEpic(null); return; }
    setEpicLoading(true);
    setEpicError(null);
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/issue/${encodeURIComponent(key)}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `Failed to load ${key}`);
      const fields = data.issue?.fields || {};
      setEpic({ key, summary: fields.summary || '', description: fields.description || '' });
    } catch (err) {
      setEpicError(err.message || `Failed to load ${key}`);
      setEpic(null);
    } finally {
      setEpicLoading(false);
    }
  }, []);

  useEffect(() => { loadEpic(selectedEpicKey); }, [selectedEpicKey, loadEpic]);

  // Load the available built-in rule templates once (same endpoint as
  // CreateEpicPanel.jsx).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/templates`);
        const data = await res.json();
        if (!cancelled && data.success && Array.isArray(data.templates)) {
          setRuleTemplates(data.templates);
          if (data.templates.includes('Story')) setRuleTemplate('Story');
        }
      } catch {
        /* non-fatal — rule file is optional */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleRulesUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(String(e.target.result || ''));
        // Validate the expected shape before storing the upload — the
        // generation endpoint (server/routes/ai.js) assumes rulesData.rules
        // is an array it can iterate/format, so a well-formed-JSON-but-wrong-
        // shape file would otherwise turn into a 500 instead of a clear
        // client-side error. Mirrors IssueForm.jsx's handleLoadCustomRules.
        if (!Array.isArray(parsed.rules)) {
          throw new Error('Invalid rules file format. Expected a "rules" array.');
        }
        setCustomRules(parsed);
        setCustomRulesName(file.name);
        setError(null);
      } catch (err) {
        setCustomRules(null);
        setCustomRulesName('');
        if (rulesInputRef.current) rulesInputRef.current.value = '';
        setError(err.message ? `Invalid rules file: ${err.message}` : 'That rule file is not valid JSON.');
      }
    };
    reader.onerror = () => setError('Could not read the rule file.');
    reader.readAsText(file);
  };

  const clearCustomRules = () => {
    setCustomRules(null);
    setCustomRulesName('');
    if (rulesInputRef.current) rulesInputRef.current.value = '';
  };

  // Restore the saved draft Stories for the selected Epic so AI suggestions
  // persist across navigation and page reloads. Runs only when the Epic changes;
  // reads the latest drafts via a ref to avoid a save->setFlow->reload loop.
  useEffect(() => {
    const key = selectedEpicKey;
    const drafts = (key && storyDraftsRef.current[key]) || [];
    lastSavedSigRef.current = JSON.stringify(drafts);
    const withUids = drafts.map((s) => ({ ...s, _uid: nextUid(), isGenerating: false }));
    setSuggestedStories(withUids);
    setExpandedStories(withUids.reduce((acc, s) => { acc[s._uid] = false; return acc; }, {}));
  }, [selectedEpicKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autosave draft Stories to the flow (debounced) whenever they change. The
  // signature guard skips the write triggered by the initial load and any
  // no-op re-renders, so we only persist genuine user edits.
  useEffect(() => {
    if (!onSaveDrafts || !selectedEpicKey) return undefined;
    const toSave = suggestedStories.map(stripDraft);
    const sig = JSON.stringify(toSave);
    if (sig === lastSavedSigRef.current) return undefined;
    const t = setTimeout(() => {
      lastSavedSigRef.current = sig;
      Promise.resolve(onSaveDrafts(selectedEpicKey, toSave)).catch(() => { /* best-effort */ });
    }, 700);
    return () => clearTimeout(t);
  }, [suggestedStories, selectedEpicKey, onSaveDrafts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Merge linked-epic keys that aren't in the fetched list so they still show.
  const query = epicSearch.trim().toLowerCase();
  const mergedEpics = [...epicOptions];
  linkedEpics.forEach((k) => {
    if (!mergedEpics.some((e) => e.key === k)) mergedEpics.push({ key: k, fields: { summary: '(linked Epic)' } });
  });
  const filteredEpics = mergedEpics.filter(
    (e) => !query || e.key.toLowerCase().includes(query) || (e.fields?.summary || '').toLowerCase().includes(query)
  );

  const epicContext = () => {
    if (!epic) return '';
    const parts = [`Parent Epic ${epic.key}: ${epic.summary}`];
    const body = htmlToText(epic.description);
    if (body) parts.push('', body);
    return parts.join('\n');
  };

  // ---- Suggested-stories handlers (mirror the Jira Issue Manager) ----

  const handleSuggest = async () => {
    if (!epic || !epic.summary) { setError('Select a parent Epic first.'); return; }
    setError(null);
    setResult(null);
    setSuggesting(true);
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/suggest-stories`, {
        method: 'POST',
        body: JSON.stringify({
          summary: epic.summary,
          description: htmlToText(epic.description) || epic.summary,
          projectKey: selectedProject,
          selectedModel,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to suggest stories');
      const processed = (data.stories || []).map((s) => {
        let criteria = [];
        if (typeof s.acceptanceCriteria === 'string') {
          criteria = s.acceptanceCriteria.split('\n').map((l) => l.trim().replace(/^\*\s*/, '')).filter(Boolean);
        } else if (Array.isArray(s.acceptanceCriteria)) {
          criteria = s.acceptanceCriteria;
        }
        return { ...s, _uid: nextUid(), acceptanceCriteria: criteria, include: true, isManual: false };
      });
      const manual = suggestedStories.filter((s) => s.isManual);
      const all = [...manual, ...processed];
      setSuggestedStories(all);
      setExpandedStories(all.reduce((acc, s) => ({ ...acc, [s._uid]: false }), {}));
    } catch (err) {
      setError(err.message || 'Failed to suggest stories');
    } finally {
      setSuggesting(false);
    }
  };

  const toggleStory = (uid) => setExpandedStories((p) => ({ ...p, [uid]: !p[uid] }));

  const updateStory = (uid, patch) => {
    setSuggestedStories((prev) => prev.map((s) => (s._uid === uid ? { ...s, ...patch } : s)));
  };

  const setInclude = (uid, include) => updateStory(uid, { include });
  const setSummary = (uid, summary) => updateStory(uid, { summary });
  const setDescription = (uid, description) => updateStory(uid, { description });

  const setCriteria = (uid, ci, value) => {
    setSuggestedStories((prev) => prev.map((s) => {
      if (s._uid !== uid) return s;
      const ac = [...(s.acceptanceCriteria || [])];
      ac[ci] = value;
      return { ...s, acceptanceCriteria: ac };
    }));
  };
  const addCriteria = (uid) => setSuggestedStories((prev) => prev.map((s) => (
    s._uid === uid ? { ...s, acceptanceCriteria: [...(s.acceptanceCriteria || []), ''] } : s
  )));
  const removeCriteria = (uid, ci) => setSuggestedStories((prev) => prev.map((s) => (
    s._uid === uid ? { ...s, acceptanceCriteria: (s.acceptanceCriteria || []).filter((_, x) => x !== ci) } : s
  )));

  const selectAll = () => setSuggestedStories((prev) => prev.map((s) => ({ ...s, include: true })));
  const deselectAll = () => setSuggestedStories((prev) => prev.map((s) => ({ ...s, include: false })));
  const removeStory = (uid) => {
    setSuggestedStories((prev) => prev.filter((s) => s._uid !== uid));
    setExpandedStories((e) => { const n = { ...e }; delete n[uid]; return n; });
    delete storyRefs.current[uid];
  };

  const addManualStory = () => {
    const uid = nextUid();
    setSuggestedStories((prev) => [...prev, { _uid: uid, summary: '', description: '', acceptanceCriteria: [''], include: true, isManual: true }]);
    setExpandedStories((e) => ({ ...e, [uid]: true }));
  };

  const generateStory = async (uid) => {
    const story = suggestedStories.find((s) => s._uid === uid);
    if (!story?.summary) return;
    updateStory(uid, { isGenerating: true });
    try {
      const body = {
        summary: story.summary,
        issueType: 'Story',
        selectedModel,
        context: epicContext(),
        existingDescription: (typeof story.description === 'string' && story.description.trim()) ? story.description : null,
      };
      if (customRules) body.customRules = customRules;
      else if (ruleTemplate) body.rulesTemplate = ruleTemplate;

      const res = await authenticatedFetch(`${apiUrl}/api/ai/generate-description-from-rules`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to generate description');
      let combined;
      if (data.userInput && data.userInput.trim()) {
        combined = `<p><b>📝 Your Input:</b></p>\n${data.userInput}\n<p><b>✨ AI Enhancement:</b></p>\n${data.description}`;
      } else {
        combined = data.description || '';
      }
      updateStory(uid, { description: combined, isGenerating: false });
    } catch (err) {
      updateStory(uid, { isGenerating: false });
      setError(err.message || 'Failed to generate description');
    }
  };

  const handleCreate = async () => {
    if (!selectedProject) { setError('Select a project at the top of the page first.'); return; }
    if (!selectedEpicKey) { setError('Select a parent Epic first.'); return; }
    const toCreate = suggestedStories.filter((s) => s.include && (s.summary || '').trim());
    if (toCreate.length === 0) { setError('Add or select at least one story to create.'); return; }
    setError(null);
    setResult(null);
    setCreating(true);
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/create-stories-for-epic`, {
        method: 'POST',
        body: JSON.stringify({ projectKey: selectedProject, epicKey: selectedEpicKey, stories: toCreate }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to create stories');
      // The server returns one result per submitted story, in the same order,
      // so correlate by position — robust even when summaries are duplicated.
      const results = data.stories || [];
      const createdKeys = results.filter((r) => r.key).map((r) => r.key);
      const createdUids = new Set();
      toCreate.forEach((s, i) => { if (results[i] && results[i].key) createdUids.add(s._uid); });
      setResult({ createdKeys, failed: results.filter((r) => !r.key).length || data.failedCount || 0 });
      if (onStoriesCreated && createdKeys.length) {
        try { await onStoriesCreated(createdKeys); } catch { /* link is best-effort */ }
      }
      // Drop the stories that were created successfully (by uid), keep failures.
      setSuggestedStories((prev) => prev.filter((s) => !createdUids.has(s._uid)));
      setExpandedStories((e) => {
        const n = { ...e };
        createdUids.forEach((uid) => { delete n[uid]; delete storyRefs.current[uid]; });
        return n;
      });
    } catch (err) {
      setError(err.message || 'Failed to create stories');
    } finally {
      setCreating(false);
    }
  };

  if (!selectedProject) {
    return <div style={s.hintBox}>Select a project at the top of the page to load its Epics.</div>;
  }

  const selectedCount = suggestedStories.filter((x) => x.include).length;

  return (
    <div style={s.wrap}>
      <div style={s.refNote}>
        Pick a parent <strong>Epic</strong>, suggest Stories from it with AI, edit them here, then create them in{' '}
        <strong>{selectedProject}</strong>. Created Stories are linked back into this flow.
      </div>

      {/* Parent Epic picker */}
      <label style={s.label}>Parent Epic</label>
      <input
        style={s.input}
        placeholder="🔍 Search Epics by key or summary…"
        value={epicSearch}
        onChange={(e) => setEpicSearch(e.target.value)}
        disabled={epicsLoading}
      />
      <select
        style={{ ...s.input, marginTop: 8 }}
        value={selectedEpicKey}
        onChange={(e) => { setSelectedEpicKey(e.target.value); setEpicSearch(''); }}
        disabled={epicsLoading}
      >
        <option value="">{epicsLoading ? '⏳ Loading Epics…' : `— Select an Epic (${filteredEpics.length}) —`}</option>
        {filteredEpics.map((e) => (
          <option key={e.key} value={e.key}>{e.key} — {e.fields?.summary || ''}</option>
        ))}
      </select>
      {epicLoading ? (
        <p style={s.help}>⏳ Loading {selectedEpicKey}…</p>
      ) : epicError ? (
        <div style={s.error}>{epicError}</div>
      ) : epic ? (
        <p style={s.help}><strong><JiraTicketLink ticketKey={epic.key} />:</strong> {epic.summary}</p>
      ) : null}

      {/* Rule file (optional) — applied to each story's ✨ "Generate description"
          call below. Mirrors the Create-Epic panel; without a selection here,
          generation falls back to the server's default "Story" rules. */}
      <label style={{ ...s.label, marginTop: 12 }}>Rule file for story descriptions (optional)</label>
      <div style={s.ruleRow}>
        <select
          style={{ ...s.input, flex: '1 1 200px' }}
          value={customRules ? '' : ruleTemplate}
          onChange={(e) => setRuleTemplate(e.target.value)}
          disabled={Boolean(customRules)}
        >
          <option value="">— Default (Story) —</option>
          {ruleTemplates.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input ref={rulesInputRef} type="file" accept=".json,application/json" onChange={handleRulesUpload} style={{ display: 'none' }} />
        <button type="button" style={s.secondaryBtn} onClick={() => rulesInputRef.current?.click()}>📎 Upload rule file</button>
      </div>
      {customRulesName && (
        <div style={s.chipWrap}>
          <span style={s.chip}>{customRulesName}<button style={s.chipX} onClick={clearCustomRules} title="Remove">×</button></span>
        </div>
      )}

      {/* Suggest */}
      <div style={s.actionRow}>
        <button
          style={{ ...s.primaryBtn, ...(suggesting || !epic ? s.btnDisabled : {}) }}
          onClick={handleSuggest}
          disabled={suggesting || !epic}
        >
          {suggesting ? 'Suggesting…' : '✨ Suggest Stories with AI'}
        </button>
        <button style={s.secondaryBtn} onClick={addManualStory}>➕ Add Story</button>
      </div>

      {/* Suggested stories (same workflow as the Jira Issue Manager) */}
      {suggestedStories.length > 0 && (
        <div style={s.storiesBox}>
          <div style={s.storiesHead}>
            <h3 style={s.storiesTitle}>Stories ({selectedCount} selected)</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={s.pill} onClick={selectAll}>✓ Select All</button>
              <button style={s.pillMuted} onClick={deselectAll}>✗ Deselect All</button>
            </div>
          </div>

          {suggestedStories.map((story) => {
            const uid = story._uid;
            return (
            <div key={uid} style={s.storyCard}>
              <div style={s.storyHeadRow}>
                <input
                  type="checkbox"
                  checked={story.include}
                  onChange={(e) => setInclude(uid, e.target.checked)}
                  style={{ marginRight: 10, cursor: 'pointer' }}
                />
                <h4 style={s.storyName} onClick={() => setInclude(uid, !story.include)}>
                  {story.summary || 'Untitled story'}
                </h4>
                <button style={s.iconBtn} onClick={() => toggleStory(uid)}>
                  {expandedStories[uid] ? '▼' : '▶'}
                </button>
                <button style={s.iconBtn} onClick={() => removeStory(uid)} title="Delete story">🗑️</button>
              </div>

              {expandedStories[uid] && (
                <div style={{ marginTop: 12 }}>
                  <label style={s.fieldLabel}>Story Title:</label>
                  <input
                    type="text"
                    value={story.summary}
                    onChange={(e) => setSummary(uid, e.target.value)}
                    style={s.input}
                  />

                  <label style={{ ...s.fieldLabel, marginTop: 12 }}>
                    Description:
                    {story.isGenerating && <span style={s.genNote}>🔄 Generating with AI…</span>}
                  </label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <RichTextEditor
                        ref={(el) => { if (el) storyRefs.current[uid] = el; }}
                        value={story.description}
                        onChange={(value) => setDescription(uid, value)}
                        placeholder="Enter story description"
                        disabled={false}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <LyricAIPopup
                        selectedModel={selectedModel}
                        currentValue={story.description}
                        onInsert={(value) => {
                          const ed = storyRefs.current[uid];
                          if (ed && ed.hasSelection && ed.hasSelection() && ed.replaceSelectedText) {
                            ed.replaceSelectedText(value);
                          } else {
                            setDescription(uid, (story.description || '') + '<br/><br/>' + value);
                          }
                        }}
                        getSelectedText={() => {
                          const ed = storyRefs.current[uid];
                          return ed && ed.getSelectedText ? ed.getSelectedText() : '';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => generateStory(uid)}
                        disabled={!story.summary || story.isGenerating}
                        style={{ ...s.genBtn, ...(!story.summary || story.isGenerating ? s.btnDisabled : {}) }}
                        title="Generate description from title"
                      >
                        ✨
                      </button>
                    </div>
                  </div>

                  <label style={{ ...s.fieldLabel, marginTop: 12 }}>Acceptance Criteria:</label>
                  {(story.acceptanceCriteria || []).map((criteria, cIndex) => (
                    <div key={cIndex} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                      <input
                        type="text"
                        value={criteria}
                        placeholder={`Acceptance criteria ${cIndex + 1}`}
                        onChange={(e) => setCriteria(uid, cIndex, e.target.value)}
                        style={s.input}
                      />
                      <button style={s.iconBtn} onClick={() => removeCriteria(uid, cIndex)} title="Remove criterion">🗑️</button>
                    </div>
                  ))}
                  <button style={s.addCriteriaBtn} onClick={() => addCriteria(uid)}>➕ Add Criterion</button>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {error && <div style={s.error}>{error}</div>}

      {/* Create */}
      <div style={s.actionRow}>
        <button
          style={{ ...s.createBtn, ...(creating || selectedCount === 0 || !selectedEpicKey ? s.btnDisabled : {}) }}
          onClick={handleCreate}
          disabled={creating || selectedCount === 0 || !selectedEpicKey}
        >
          {creating ? 'Creating…' : `🚀 Create ${selectedCount || ''} Stor${selectedCount === 1 ? 'y' : 'ies'} under ${selectedEpicKey || 'Epic'}`}
        </button>
      </div>

      {result && (
        <div style={s.success}>
          {result.createdKeys.length > 0
            ? <>✓ Created <strong>{result.createdKeys.map((k, i) => (
                <React.Fragment key={k}>{i > 0 ? ', ' : ''}<JiraTicketLink ticketKey={k} /></React.Fragment>
              ))}</strong> and linked them into this flow.</>
            : 'No stories were created.'}
          {result.failed > 0 && <div style={{ color: '#9b1c1c', marginTop: 6 }}>⚠ {result.failed} story(ies) failed — see server logs.</div>}
        </div>
      )}
    </div>
  );
};

const s = {
  wrap: { marginTop: 14, borderTop: '1px dashed #d9d9d9', paddingTop: 14 },
  refNote: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#1e40af', marginBottom: 12 },
  hintBox: { marginTop: 14, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 8, padding: '10px 12px', fontSize: 13 },
  label: { display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14, color: '#333' },
  fieldLabel: { display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 13, color: '#333' },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  ruleRow: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  chipWrap: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#e8f0fe', color: '#1a56db', padding: '4px 8px', borderRadius: 12, fontSize: 13 },
  chipX: { background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 0 },
  actionRow: { marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  primaryBtn: { background: '#1a56db', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  secondaryBtn: { background: '#fff', border: '1px solid #ccc', padding: '8px 12px', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#333' },
  createBtn: { background: '#047857', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  help: { color: '#777', fontSize: 12, margin: '6px 0 0' },
  storiesBox: { marginTop: 14, padding: 12, background: '#f8f9fa', border: '2px solid #28a745', borderRadius: 8 },
  storiesHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  storiesTitle: { margin: 0, color: '#28a745', fontSize: 15 },
  pill: { padding: '4px 10px', background: '#fff', color: '#28a745', border: '1px solid #28a745', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 500 },
  pillMuted: { padding: '4px 10px', background: '#fff', color: '#666', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 500 },
  storyCard: { marginBottom: 12, padding: 12, background: '#fff', border: '1px solid #dee2e6', borderRadius: 6 },
  storyHeadRow: { display: 'flex', alignItems: 'center' },
  storyName: { margin: 0, flex: 1, color: '#333', cursor: 'pointer', userSelect: 'none', fontSize: 14 },
  iconBtn: { background: '#f8f9fa', border: '1px solid #ccc', borderRadius: 4, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginLeft: 6, fontSize: 14 },
  genNote: { fontSize: 12, color: '#ff9800', fontWeight: 700, marginLeft: 8 },
  genBtn: { background: '#28a745', color: '#fff', border: 'none', borderRadius: 4, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 15 },
  addCriteriaBtn: { padding: '4px 10px', background: '#f8f9fa', color: '#007bff', border: '1px solid #007bff', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginTop: 4 },
  error: { background: '#fde8e8', color: '#9b1c1c', padding: '10px 12px', borderRadius: 6, margin: '10px 0', fontSize: 14 },
  success: { background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', padding: '10px 12px', borderRadius: 8, marginTop: 12, fontSize: 14 },
};

export default CreateStoriesPanel;
